import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import HistoryCard from '@/components/member-profile/HistoryCard';
import PageSkeleton from '@/components/member-profile/PageSkeleton';
import {
  CATEGORY_LABELS,
  CLASSIFICATION_LABELS,
  GENDER_LABELS,
  RANK_COLORS,
  formatDate,
} from '@/components/member-profile/labels';
import { ROUTES } from '@/config/routes';
import { useHydration } from '@/hooks/useHydration';
import { getUserHistoryApi, getUserProfileApi, getUserStatsApi } from '@/lib/api/usersApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { COMPETITION_TYPE_LABELS as TYPE_LABELS } from '@shared';
import { FederalAgeCategory, Gender } from '@shared/enums/domain.enum';
import { computeAgeCategory } from '@shared/utils/federation.utils';
import { useQuery } from '@tanstack/react-query';
import {
  Award,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Crown,
  EyeOff,
  History,
  LogIn,
  Settings,
  Shield,
  Star,
  Trophy,
  Users,
} from 'lucide-react';

// ─── Page ─────────────────────────────────────────────────────────

export default function MemberProfile() {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const ADMIN_URL = import.meta.env.VITE_URL_ADMIN as string | undefined;

  const hydrated = useHydration();
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const profileId = Number(idParam);
  const isValidId = !isNaN(profileId) && profileId > 0;
  const isOwnProfile = isAuthenticated && !!user && user.id === profileId;

  const [activeTab, setActiveTab] = useState<'stats' | 'history' | 'ffpjp'>('stats');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyYear, setHistoryYear] = useState<number | undefined>(undefined);

  // ─── Queries (toujours via endpoints publics /:id) ────────────

  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
  } = useQuery({
    queryKey: queryKeys.users.profile(profileId),
    queryFn: () => getUserProfileApi(profileId),
    enabled: isAuthenticated && isValidId,
    retry: false,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.users.stats(profileId),
    queryFn: () => getUserStatsApi(profileId),
    enabled: isAuthenticated && isValidId,
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: queryKeys.users.history(profileId, { page: historyPage, limit: 20, year: historyYear }),
    queryFn: () => getUserHistoryApi(profileId, { page: historyPage, limit: 20, year: historyYear }),
    enabled: isAuthenticated && isValidId && activeTab === 'history',
  });

  // ─── Guards ───────────────────────────────────────────────────

  // Attendre la rehydration Zustand avant tout rendu conditionnel
  if (!hydrated) return <PageSkeleton />;

  if (!isAuthenticated) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
        <div className='text-center max-w-md'>
          <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
            <LogIn className='w-8 h-8 text-blue-600' />
          </div>
          <h1 className='text-2xl font-bold text-slate-900 mb-2'>Connexion requise</h1>
          <p className='text-slate-500 mb-6'>Connectez-vous pour accéder au profil joueur.</p>
          <Link
            to={ROUTES.LOGIN}
            className='inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
          >
            Se connecter
          </Link>
        </div>
      </div>
    );
  }

  if (profileLoading) return <PageSkeleton />;

  if (profileError || !profile) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center px-6'>
        <div className='text-center max-w-md'>
          <p className='text-slate-500 text-lg'>Profil introuvable.</p>
          <button onClick={() => navigate(-1)} className='mt-4 text-sm text-blue-600 hover:text-blue-700 font-semibold'>
            Retour
          </button>
        </div>
      </div>
    );
  }

  // ─── Profil privé (sauf si c'est le sien) ─────────────────────

  if (!profile.isProfilePublic && !isOwnProfile) {
    return (
      <div className='bg-[#F8FAFC] text-slate-900 font-sans min-h-screen'>
        <PageMeta
          title={`${profile.firstName ?? ''} ${profile.lastName ?? ''}`}
          description='Ce profil joueur est privé.'
          robots='noindex, nofollow'
        />

        <section className='pt-32 pb-8 px-6'>
          <div className='max-w-4xl mx-auto'>
            <button
              onClick={() => navigate(-1)}
              className='flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6'
            >
              <ChevronLeft className='w-4 h-4' /> Retour
            </button>

            {/* Header avec infos minimales */}
            <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6'>
              <div className='w-20 h-20 rounded-2xl bg-slate-200 flex items-center justify-center shrink-0 blur-sm opacity-60'>
                <span className='text-2xl font-extrabold text-slate-400'>
                  {(profile.firstName?.[0] ?? '?').toUpperCase()}
                  {(profile.lastName?.[0] ?? '').toUpperCase()}
                </span>
              </div>
              <div className='flex-1 min-w-0'>
                <h1 className='text-2xl font-extrabold text-slate-900'>
                  {profile.firstName} {profile.lastName}
                </h1>
                {profile.username && <p className='text-slate-400 text-sm'>@{profile.username}</p>}
              </div>
            </div>
          </div>
        </section>

        {/* Private message */}
        <section className='px-6 py-8'>
          <div className='max-w-2xl mx-auto'>
            <div className='bg-white rounded-3xl border border-slate-200 shadow-sm p-12 text-center'>
              <div className='w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6'>
                <EyeOff className='w-8 h-8 text-slate-400' />
              </div>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-3'>Profil privé</h2>
              <p className='text-slate-500 leading-relaxed mb-8'>
                Ce joueur a choisi de rendre son profil privé. Les statistiques et l&apos;historique ne sont pas
                accessibles.
              </p>
              <button
                onClick={() => navigate(-1)}
                className='inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
              >
                Retour
              </button>
            </div>
          </div>
        </section>

        {/* Blurred skeleton */}
        <section className='px-6 pb-16'>
          <div className='max-w-4xl mx-auto blur-sm opacity-30 pointer-events-none select-none'>
            <div className='space-y-4'>
              <div className='bg-white rounded-2xl border border-slate-100 p-6'>
                <div className='h-5 bg-slate-200 rounded w-1/4 mb-4' />
                <div className='grid grid-cols-3 gap-4'>
                  <div className='h-20 bg-slate-100 rounded-xl' />
                  <div className='h-20 bg-slate-100 rounded-xl' />
                  <div className='h-20 bg-slate-100 rounded-xl' />
                </div>
              </div>
              <div className='bg-white rounded-2xl border border-slate-100 p-6'>
                <div className='h-5 bg-slate-200 rounded w-1/3 mb-4' />
                <div className='space-y-3'>
                  <div className='h-12 bg-slate-100 rounded-xl' />
                  <div className='h-12 bg-slate-100 rounded-xl' />
                  <div className='h-12 bg-slate-100 rounded-xl' />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ─── Données affichage ────────────────────────────────────────

  const ageCategory = profile.birthDate
    ? (() => {
        try {
          return computeAgeCategory(new Date(profile.birthDate)) as FederalAgeCategory;
        } catch {
          return null;
        }
      })()
    : null;

  const overview = stats?.overview;

  // L'API renvoie une forme partielle selon le niveau d'accès (PUBLIC / OVERVIEW / FULL) :
  // badges/byType/recentResults/topPartners peuvent être absents. Défauts sûrs pour éviter
  // tout accès à `.length`/`.some` sur undefined.
  const badges = stats?.badges ?? [];
  const byType = stats?.byType ?? [];
  const recentResults = stats?.recentResults ?? [];
  const topPartners = stats?.topPartners ?? [];

  const tabs = [
    { id: 'stats' as const, label: 'Statistiques', icon: <BarChart2 className='w-4 h-4' /> },
    { id: 'history' as const, label: 'Historique', icon: <History className='w-4 h-4' /> },
    { id: 'ffpjp' as const, label: 'Profil fédéral', icon: <Shield className='w-4 h-4' /> },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans min-h-screen'>
      <PageMeta
        title={isOwnProfile ? 'Mon profil' : `Profil — ${profile.firstName ?? ''} ${profile.lastName ?? ''}`}
        description='Profil joueur BoulesConnect'
        robots='noindex, nofollow'
      />

      {/* Hero header */}
      <section className='pt-32 pb-8 px-6'>
        <div className='max-w-4xl mx-auto'>
          <button
            onClick={() => navigate(-1)}
            className='flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6'
          >
            <ChevronLeft className='w-4 h-4' /> Retour
          </button>

          <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6'>
            {/* Avatar */}
            {profile.files?.[0] ? (
              <img
                src={getFileUrl(profile.files[0])}
                alt={`${profile.firstName ?? ''} ${profile.lastName ?? ''}`}
                className='w-20 h-20 rounded-2xl object-cover shrink-0 shadow-sm'
              />
            ) : (
              <div className='w-20 h-20 rounded-2xl bg-blue-600 flex items-center justify-center shrink-0'>
                <span className='text-2xl font-extrabold text-white'>
                  {(profile.firstName?.[0] ?? '?').toUpperCase()}
                  {(profile.lastName?.[0] ?? '').toUpperCase()}
                </span>
              </div>
            )}

            <div className='flex-1 min-w-0'>
              <div className='flex flex-wrap items-center gap-3 mb-1'>
                <h1 className='text-2xl font-extrabold text-slate-900'>
                  {profile.firstName} {profile.lastName}
                </h1>
                {ageCategory && (
                  <span className='px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold border border-indigo-200'>
                    {CATEGORY_LABELS[ageCategory]}
                  </span>
                )}
              </div>
              {profile.username && <p className='text-slate-400 text-sm'>@{profile.username}</p>}
              {profile.club && (
                <p className='text-sm text-slate-500 mt-0.5'>
                  Club :{' '}
                  <Link
                    to={ROUTES.CLUB_PROFILE.replace(':id', String(profile.club.id))}
                    className='font-semibold text-blue-600 hover:text-blue-700'
                  >
                    {profile.club.name}
                  </Link>
                </p>
              )}
              {isOwnProfile && (
                <div className='mt-2 flex flex-wrap items-center gap-2'>
                  {/* Badge visibilité */}
                  <span
                    className={[
                      'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border',
                      profile.isProfilePublic
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-600 border-slate-200',
                    ].join(' ')}
                    title={
                      profile.isProfilePublic
                        ? 'Votre profil est visible par les autres membres connectés.'
                        : 'Votre profil n’est visible que par vous.'
                    }
                  >
                    {profile.isProfilePublic ? (
                      <>
                        <Users className='w-3.5 h-3.5' />
                        Profil public
                      </>
                    ) : (
                      <>
                        <EyeOff className='w-3.5 h-3.5' />
                        Profil privé
                      </>
                    )}
                  </span>

                  <a
                    href={`${ADMIN_URL}/player/preferences?tab=privacy`}
                    className='inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700'
                    target={`_blank`}
                  >
                    <Settings className='w-3.5 h-3.5' />
                    Modifier
                  </a>
                  {/* Aide + action */}
                  <span className='text-xs text-slate-400'>
                    {profile.isProfilePublic
                      ? 'Les autres membres peuvent voir vos stats et votre historique.'
                      : 'Les autres membres ne voient pas vos stats ni votre historique.'}
                  </span>
                </div>
              )}
            </div>

            <div className='flex flex-col items-end gap-3 shrink-0'>
              {/* Stats rapides */}
              {overview && (
                <div className='flex gap-6'>
                  <div className='text-center'>
                    <p className='text-2xl font-extrabold text-slate-900'>{overview.totalCompetitions}</p>
                    <p className='text-xs text-slate-400 font-medium'>Concours</p>
                  </div>
                  <div className='text-center'>
                    <p className='text-2xl font-extrabold text-yellow-600'>{overview.wins}</p>
                    <p className='text-xs text-slate-400 font-medium'>Victoires</p>
                  </div>
                  <div className='text-center'>
                    <p className='text-2xl font-extrabold text-orange-500'>{overview.podiums}</p>
                    <p className='text-xs text-slate-400 font-medium'>Podiums</p>
                  </div>
                </div>
              )}
              {/* Lien vers mon espace si profil propre */}
              {isOwnProfile && (
                <Link
                  to={ROUTES.ROLE_LANDING}
                  className='flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold transition-colors'
                >
                  <Settings className='w-4 h-4' />
                  Gérer mon compte
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Tabs + Content */}
      <section className='px-6 pb-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='flex gap-1 bg-slate-100 rounded-2xl p-1 mb-8 w-fit'>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                  activeTab === tab.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* ─── Onglet Statistiques ────────────────────────────── */}
          {activeTab === 'stats' && (
            <div className='space-y-6'>
              {statsLoading ? (
                <div className='space-y-4'>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className='h-32 animate-pulse rounded-2xl bg-slate-200' />
                  ))}
                </div>
              ) : !stats || overview?.totalCompetitions === 0 ? (
                <div className='text-center py-12'>
                  <Crown className='w-12 h-12 text-slate-200 mx-auto mb-3' />
                  <p className='text-slate-400 font-medium'>Aucune participation enregistrée.</p>
                  {isOwnProfile && (
                    <Link
                      to={ROUTES.SEARCH_CONCOURS}
                      className='inline-flex items-center gap-2 mt-4 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors'
                    >
                      Trouver un concours
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  {/* Badges obtenus */}
                  {badges.some(b => b.earned) && (
                    <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-6'>
                      <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                        <Award className='w-5 h-5 text-yellow-500' /> Badges obtenus
                      </h3>
                      <div className='flex flex-wrap gap-3'>
                        {badges
                          .filter(b => b.earned)
                          .map(badge => (
                            <div
                              key={badge.id}
                              className='flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-sm font-semibold text-amber-700'
                            >
                              <span>{badge.emoji}</span>
                              {badge.label}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Par format */}
                  {byType.length > 0 && (
                    <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-6'>
                      <h3 className='font-bold text-slate-900 mb-4'>Par format</h3>
                      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                        {byType.map(bt => (
                          <div key={bt.type} className='rounded-xl border border-slate-100 p-4 bg-slate-50 text-center'>
                            <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2'>
                              {TYPE_LABELS[bt.type] ?? bt.type}
                            </p>
                            <p className='text-2xl font-extrabold text-slate-900'>{bt.total}</p>
                            <p className='text-sm text-slate-500'>
                              {bt.wins} victoire{bt.wins > 1 ? 's' : ''} — {bt.winRate}%
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Derniers résultats */}
                  {recentResults.length > 0 && (
                    <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-6'>
                      <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                        <Trophy className='w-5 h-5 text-blue-500' /> Derniers résultats
                      </h3>
                      <div className='space-y-3'>
                        {recentResults.map((r, i) => (
                          <div
                            key={i}
                            className='flex items-center justify-between py-3 border-b border-slate-50 last:border-0'
                          >
                            <div>
                              <p className='font-semibold text-slate-900 text-sm'>{r.competitionName}</p>
                              <p className='text-xs text-slate-400'>{formatDate(r.date)}</p>
                            </div>
                            <div
                              className={`text-sm font-bold px-2.5 py-1 rounded-lg border ${
                                r.rank !== null && r.rank <= 3
                                  ? RANK_COLORS[r.rank]
                                  : 'bg-slate-50 text-slate-500 border-slate-100'
                              }`}
                            >
                              {r.rank !== null ? `${r.rank}e` : '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Partenaires fréquents */}
                  {topPartners.length > 0 && (
                    <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-6'>
                      <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                        <Users className='w-5 h-5 text-emerald-500' /> Partenaires fréquents
                      </h3>
                      <div className='space-y-3'>
                        {topPartners.map(p => (
                          <div key={p.userId} className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                              <div className='w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold text-sm'>
                                {(p.firstName?.[0] ?? '?').toUpperCase()}
                                {(p.lastName?.[0] ?? '').toUpperCase()}
                              </div>
                              <div>
                                <p className='font-semibold text-slate-900 text-sm'>
                                  {p.firstName} {p.lastName}
                                </p>
                                <p className='text-xs text-slate-400'>{p.sharedTeams} concours ensemble</p>
                              </div>
                            </div>
                            <div className='flex items-center gap-1 text-sm font-semibold text-yellow-600'>
                              <Star className='w-4 h-4' />
                              {p.winRate}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ─── Onglet Historique ──────────────────────────────── */}
          {activeTab === 'history' && (
            <div className='space-y-6'>
              <div className='flex flex-wrap items-center gap-3'>
                <select
                  value={historyYear ?? ''}
                  onChange={e => {
                    setHistoryYear(e.target.value ? Number(e.target.value) : undefined);
                    setHistoryPage(1);
                  }}
                  className='bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100'
                >
                  <option value=''>Toutes les années</option>
                  {yearOptions.map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                {history && (
                  <span className='text-sm text-slate-400'>
                    {history.meta.total} concours
                    {history.stats?.wins > 0 && ` — ${history.stats.wins} victoire${history.stats.wins > 1 ? 's' : ''}`}
                  </span>
                )}
              </div>

              {historyLoading ? (
                <div className='space-y-3'>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className='h-24 animate-pulse rounded-2xl bg-slate-200' />
                  ))}
                </div>
              ) : !history || history.data.length === 0 ? (
                <div className='text-center py-12'>
                  <History className='w-12 h-12 text-slate-200 mx-auto mb-3' />
                  <p className='text-slate-400 font-medium'>Aucune participation trouvée</p>
                  {historyYear && (
                    <button
                      onClick={() => setHistoryYear(undefined)}
                      className='mt-3 text-sm text-blue-600 hover:text-blue-700 font-semibold'
                    >
                      Voir toutes les années
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className='space-y-3'>
                    {history.data.map(item => (
                      <HistoryCard key={item.teamId} item={item} />
                    ))}
                  </div>
                  {history.meta.totalPages > 1 && (
                    <div className='flex items-center justify-center gap-3'>
                      <button
                        onClick={() => setHistoryPage(p => p - 1)}
                        disabled={historyPage === 1}
                        className='p-2 rounded-xl bg-white border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors'
                      >
                        <ChevronLeft className='w-4 h-4' />
                      </button>
                      <span className='text-sm font-semibold text-slate-600'>
                        {historyPage} / {history.meta.totalPages}
                      </span>
                      <button
                        onClick={() => setHistoryPage(p => p + 1)}
                        disabled={historyPage >= history.meta.totalPages}
                        className='p-2 rounded-xl bg-white border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors'
                      >
                        <ChevronRight className='w-4 h-4' />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ─── Onglet Profil fédéral (lecture seule) ──────────── */}
          {activeTab === 'ffpjp' && (
            <div className='space-y-6'>
              <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-6'>
                <div className='flex items-center gap-3 mb-6'>
                  <div className='w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center'>
                    <Shield className='w-5 h-5 text-indigo-600' />
                  </div>
                  <div>
                    <h3 className='font-bold text-slate-900'>Données fédérales (FFPJP)</h3>
                    <p className='text-xs text-slate-400'>Utilisées pour les concours fédéraux</p>
                  </div>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
                  <div>
                    <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1'>
                      Date de naissance
                    </p>
                    <p className='font-semibold text-slate-900'>
                      {profile.birthDate ? (
                        formatDate(profile.birthDate)
                      ) : (
                        <span className='text-slate-400 italic'>Non renseignée</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1'>Sexe</p>
                    <p className='font-semibold text-slate-900'>
                      {profile.gender ? (
                        GENDER_LABELS[profile.gender as Gender]
                      ) : (
                        <span className='text-slate-400 italic'>Non renseigné</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1'>
                      Catégorie d&apos;âge
                    </p>
                    {ageCategory ? (
                      <span className='inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-bold border border-indigo-200'>
                        {CATEGORY_LABELS[ageCategory]}
                      </span>
                    ) : (
                      <span className='text-slate-400 italic text-sm'>
                        {profile.birthDate ? 'Calcul impossible' : 'Date de naissance requise'}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1'>Classification</p>
                    <p className='font-semibold text-slate-900'>
                      {profile.federalClassification ? (
                        (CLASSIFICATION_LABELS[profile.federalClassification] ?? profile.federalClassification)
                      ) : (
                        <span className='text-slate-400 italic'>Non renseignée</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {isOwnProfile && (
                <div className='bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between gap-4'>
                  <p className='text-sm text-blue-700 font-medium'>
                    Pour modifier vos données fédérales, rendez-vous dans votre espace personnel.
                  </p>
                  <Link
                    to={ROUTES.ROLE_LANDING}
                    className='shrink-0 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors'
                  >
                    <Settings className='w-4 h-4' />
                    Mon espace
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
